import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ExpandMore,
  HelpOutline,
  LockOutlined,
  LockReset,
  MailOutline,
  SupportAgent,
} from '@mui/icons-material';
import { customFetcher } from '../../hooks/useFetch';
import { DialogTopBar } from '../core/DialogTopBar';

interface HelpDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface IHelpEntry {
  id: string;
  icon: React.ReactNode;
  question: string;
  answer: React.ReactNode;
  contactTitle: string;
}

export const HelpDialog: React.FC<HelpDialogProps> = ({ isOpen, onClose }) => {
  const [formError, setFormError] = useState('');
  const [supportEmailInputValue, setSupportEmailInputValue] = useState('');
  const [
    supportTelephoneNumberInputValue,
    setSupportTelephoneNumberInputValue,
  ] = useState('');
  const [supportSummaryInputValue, setSupportSummaryInputValue] = useState('');
  const [supportContactAllowedInputValue, setSupportContactAllowedInputValue] =
    useState(false);
  const [sendToDev, setSendToDev] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(false);

  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleSendSupportMail = () => {
    if (
      !supportEmailInputValue ||
      !supportTelephoneNumberInputValue ||
      !supportSummaryInputValue
    ) {
      setFormError('Bitte füllen Sie alle Felder aus, bevor Sie fortfahren.');
      return;
    }
    setFormError('');

    customFetcher({
      endpoint: '/system/support',
      method: 'POST',
      body: {
        email: supportEmailInputValue,
        telephonenumber: supportTelephoneNumberInputValue,
        description: supportSummaryInputValue,
        allowSupportContact: supportContactAllowedInputValue,
      },
    }).catch(console.error);

    setIsContactDialogOpen(false);
    onClose();
    clearSupportInputs();
  };

  const clearSupportInputs = () => {
    setSendToDev(false);
    setSupportContactAllowedInputValue(false);
    setSupportEmailInputValue('');
    setSupportTelephoneNumberInputValue('');
    setSupportSummaryInputValue('');
  };

  const openContactForm = (title: string) => {
    setSupportSummaryInputValue(title);
    setIsContactDialogOpen(true);
  };

  const helpEntries: IHelpEntry[] = [
    {
      id: 'account_locked',
      icon: <LockOutlined fontSize='small' />,
      question: 'Mein Konto ist gesperrt. Wie wird es wieder freigeschaltet?',
      contactTitle: 'Konto gesperrt',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Ihr Konto wird automatisch gesperrt, wenn es mehrere fehlgeschlagene
            Anmeldeversuche gab. Aus Sicherheitsgründen kann Ihr Konto nur
            manuell von den Mitarbeitenden Ihres Notarbüros entsperrt werden.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Bitte wenden Sie sich hierfür direkt an Ihr Notarbüro. Das Team
            schaltet Ihr Konto schnellstmöglich wieder frei.
          </Typography>
        </>
      ),
    },
    {
      id: 'no_2fa',
      icon: <MailOutline fontSize='small' />,
      question:
        'Ich habe keinen Authentifizierungsschlüssel erhalten. Was kann ich tun?',
      contactTitle: 'Ich erhalte keinen 2FA-Code',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Zum Schutz Ihres Kontos verwenden wir eine
            2-Faktor-Authentifizierung. Nach Eingabe Ihrer E-Mail-Adresse und
            Ihres Passworts erhalten Sie einen Authentifizierungsschlüssel per
            E-Mail. Es kann einige Minuten dauern, bis dieser bei Ihnen
            eintrifft — bitte prüfen Sie auch den <strong>Spam-Ordner</strong>.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Sollte auch nach mehreren Minuten keine E-Mail eintreffen, wenden
            Sie sich bitte an Ihr Notarbüro.
          </Typography>
        </>
      ),
    },
    {
      id: 'password_reset',
      icon: <LockReset fontSize='small' />,
      question: 'Ich möchte mein Passwort zurücksetzen. Wie gehe ich vor?',
      contactTitle: 'Passwort kann nicht zurückgesetzt werden',
      answer: (
        <>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Falls Sie Ihr Passwort vergessen haben, klicken Sie auf der
            Anmeldeseite auf <strong>Passwort zurücksetzen</strong>. Geben Sie
            anschließend Ihre E-Mail-Adresse ein und folgen Sie den Anweisungen
            in der E-Mail, die Sie erhalten.
          </Typography>
          <Typography variant='body2' sx={{ mb: 1.5 }}>
            Nach erfolgreicher Anmeldung können Sie Ihr Passwort jederzeit unter
            <strong> Mein Benutzerkonto</strong> ändern.
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Sollte das Zurücksetzen nicht funktionieren, hilft Ihnen Ihr
            Notarbüro gerne weiter.
          </Typography>
        </>
      ),
    },
  ];

  return (
    <>
      {/* Haupt-Hilfe-Dialog */}
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: theme => ({
            borderRadius: 3,
            boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.18)}`,
          }),
        }}
      >
        <DialogTopBar
          icon={<HelpOutline />}
          title='Hilfe rund um die Anmeldung'
          subtitle='Antworten zu typischen Fragen zur Anmeldung im Mandantenportal'
          onClose={onClose}
        />

        <DialogContent
          sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'background.default' }}
        >
          <Stack spacing={1.25} sx={{ mt: { xs: 2, sm: 2.5 } }}>
            {helpEntries.map(entry => {
              const isExpanded = expanded === entry.id;
              return (
                <Accordion
                  key={entry.id}
                  expanded={isExpanded}
                  onChange={handleChange(entry.id)}
                  disableGutters
                  elevation={0}
                  sx={theme => ({
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      isExpanded ? 0.28 : 0.1
                    )}`,
                    borderRadius: '12px !important',
                    overflow: 'hidden',
                    backgroundColor: isExpanded
                      ? alpha(theme.palette.primary.main, 0.02)
                      : 'background.paper',
                    transition: 'all 0.18s ease',
                    '&:before': { display: 'none' },
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.24),
                    },
                  })}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      px: 2,
                      py: 0.5,
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': {
                        my: 1.25,
                        alignItems: 'center',
                        gap: 1.5,
                      },
                    }}
                  >
                    <Box
                      sx={theme => ({
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.1
                        ),
                      })}
                    >
                      {entry.icon}
                    </Box>
                    <Typography
                      variant='subtitle2'
                      sx={{ fontWeight: 600, lineHeight: 1.3 }}
                    >
                      {entry.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={theme => ({
                      px: 2,
                      pt: 0,
                      pb: 2,
                      pl: 5.75,
                      borderTop: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.06
                      )}`,
                    })}
                  >
                    <Box sx={{ pt: 1.5 }}>{entry.answer}</Box>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        size='small'
                        variant='outlined'
                        startIcon={<SupportAgent fontSize='small' />}
                        onClick={() => openContactForm(entry.contactTitle)}
                      >
                        Support kontaktieren
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button onClick={onClose} variant='contained'>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kontaktformular-Dialog */}
      <Dialog
        open={isContactDialogOpen}
        onClose={() => setIsContactDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: theme => ({
            borderRadius: 3,
            boxShadow: `0 24px 64px ${alpha(theme.palette.primary.main, 0.18)}`,
          }),
        }}
      >
        <DialogTopBar
          icon={<SupportAgent />}
          title='Support kontaktieren'
          subtitle='Schildern Sie Ihr Anliegen — wir melden uns zeitnah zurück'
          onClose={() => setIsContactDialogOpen(false)}
        />

        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2} sx={{ mt: { xs: 2, sm: 2.5 } }}>
            {formError && <Alert severity='error'>{formError}</Alert>}
            <Alert severity='info' icon={<HelpOutline fontSize='small' />}>
              <Typography variant='body2' sx={{ mb: 0.5 }}>
                Für <strong>Kontosperrungen</strong>, fehlende
                <strong> Authentifizierungsschlüssel</strong> oder das
                <strong> Zurücksetzen Ihres Passworts</strong> wenden Sie sich
                bitte direkt an Ihr Notarbüro.
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Konnten die obenstehenden Hinweise nicht weiterhelfen, leiten
                wir Ihre Anfrage gerne an LeXtorByte UG weiter.
              </Typography>
            </Alert>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: '100%' }}
            >
              <TextField
                value={supportEmailInputValue}
                fullWidth
                onChange={v => setSupportEmailInputValue(v.target.value)}
                placeholder='ihre.adresse@beispiel.de'
                label='E-Mail'
                required
              />
              <TextField
                value={supportTelephoneNumberInputValue}
                fullWidth
                onChange={v =>
                  setSupportTelephoneNumberInputValue(v.target.value)
                }
                placeholder='+49 …'
                label='Telefonnummer'
                required
              />
            </Stack>
            <TextField
              value={supportSummaryInputValue}
              fullWidth
              onChange={v => setSupportSummaryInputValue(v.target.value)}
              multiline
              minRows={3}
              maxRows={6}
              label='Problembeschreibung'
              placeholder='Beschreiben Sie Ihr Anliegen so genau wie möglich.'
              required
            />

            <Box
              sx={theme => ({
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
              })}
            >
              <FormControlLabel
                label={
                  <Typography variant='body2' component='span'>
                    Anfrage an den Softwarehersteller{' '}
                    <strong>(LeXtorByte UG)</strong> senden
                  </Typography>
                }
                control={
                  <Checkbox
                    checked={sendToDev}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setSendToDev(event.target.checked);
                      setSupportContactAllowedInputValue(false);
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                sx={{ alignItems: 'center', m: 0 }}
              />
              <Collapse in={sendToDev}>
                <Box sx={{ mt: 1 }}>
                  <Alert severity='info' icon={false} variant='outlined'>
                    <FormControlLabel
                      label={
                        <Typography variant='body2' component='span'>
                          Ich erkläre mich damit einverstanden, dass meine Daten
                          von der LeXtorByte UG (haftungsbeschränkt) zum Zwecke
                          der Kontaktaufnahme und Hilfestellung verarbeitet
                          werden dürfen.
                        </Typography>
                      }
                      control={
                        <Checkbox
                          checked={supportContactAllowedInputValue}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            setSupportContactAllowedInputValue(
                              event.target.checked
                            )
                          }
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                  </Alert>
                </Box>
              </Collapse>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button onClick={() => setIsContactDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='primary'
            disabled={sendToDev && !supportContactAllowedInputValue}
            onClick={handleSendSupportMail}
          >
            Abschicken
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
